import { useMemo, useState } from 'react'
import { Sparkles, Check, Plus, X, Loader2, ChevronRight } from 'lucide-react'
import type { ScopeProposal, ScopeDecisions } from '@/types'

interface ScopeConfirmationProps {
  proposal: ScopeProposal
  /** Called with the confirmed decisions (selections, exclusions, additions, notes). */
  onConfirm: (decisions: ScopeDecisions) => void
  /** Skip the round entirely — generate from the brief without scope decisions. */
  onSkip?: () => void
  /** Disables all inputs (e.g. while the FRS is being generated). */
  busy?: boolean
  /** Tighter paddings for narrow panels (Auto-Agent pane / dock). */
  compact?: boolean
  confirmLabel?: string
}

/**
 * The pre-FRS "confirm scope & complexity" interface — Claude-Code-style
 * question answering: the AI's proposed scope rendered as selectable option
 * cards (multi-select checkboxes or single-select radios), a per-question
 * "add your own" input for points the proposal missed, and a free-text notes
 * box. Shared by DescribeAppPage (REST round) and the Auto-Agent build mode
 * (WebSocket question round).
 */
export default function ScopeConfirmation({
  proposal,
  onConfirm,
  onSkip,
  busy = false,
  compact = false,
  confirmLabel = 'Confirm & continue',
}: ScopeConfirmationProps) {
  // qid -> set of selected option labels (seeded from the proposal defaults)
  const initialSelections = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const q of proposal.questions) {
      map[q.id] = q.options.filter((o) => o.selected).map((o) => o.label)
      // A single-select question must have exactly one choice active.
      if (!q.multi && map[q.id].length > 1) map[q.id] = [map[q.id][0]]
      if (!q.multi && map[q.id].length === 0 && q.options.length > 0) {
        map[q.id] = [q.options[0].label]
      }
    }
    return map
  }, [proposal])

  const [selections, setSelections] = useState<Record<string, string[]>>(initialSelections)
  const [customs, setCustoms] = useState<Record<string, string[]>>({})
  const [customDrafts, setCustomDrafts] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState('')

  const toggleOption = (qid: string, label: string, multi: boolean) => {
    if (busy) return
    setSelections((prev) => {
      const current = prev[qid] || []
      if (!multi) return { ...prev, [qid]: [label] }
      return {
        ...prev,
        [qid]: current.includes(label)
          ? current.filter((l) => l !== label)
          : [...current, label],
      }
    })
  }

  const addCustom = (qid: string) => {
    const value = (customDrafts[qid] || '').trim()
    if (!value) return
    setCustoms((prev) => {
      const current = prev[qid] || []
      if (current.some((c) => c.toLowerCase() === value.toLowerCase())) return prev
      return { ...prev, [qid]: [...current, value] }
    })
    setCustomDrafts((prev) => ({ ...prev, [qid]: '' }))
  }

  const removeCustom = (qid: string, value: string) => {
    setCustoms((prev) => ({
      ...prev,
      [qid]: (prev[qid] || []).filter((c) => c !== value),
    }))
  }

  const handleConfirm = () => {
    const decisions: ScopeDecisions = {
      answers: proposal.questions.map((q) => {
        const selected = selections[q.id] || []
        return {
          id: q.id,
          question: q.question,
          selected,
          // Only multi-select questions carry explicit exclusions — for a
          // single-select (e.g. complexity) the unchosen levels aren't
          // "declined scope", they're just the other radio options.
          deselected: q.multi
            ? q.options.map((o) => o.label).filter((l) => !selected.includes(l))
            : [],
          custom: customs[q.id] || [],
        }
      }),
      additional_notes: notes.trim(),
    }
    onConfirm(decisions)
  }

  const pad = compact ? '0.75rem' : '1.25rem'

  return (
    <div
      className={compact && !busy ? 'agent-ask-attention' : undefined}
      style={{
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-accent)',
        borderRadius: 'var(--radius-lg)',
        padding: pad,
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '0.75rem' : '1rem',
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
          <Sparkles size={14} style={{ color: 'var(--color-accent)' }} />
          <span style={{ fontSize: compact ? '0.8125rem' : '0.9375rem', fontWeight: 600 }}>
            Confirm scope &amp; complexity
          </span>
        </div>
        {proposal.summary && (
          <p style={{
            fontSize: compact ? '0.8125rem' : '0.875rem',
            color: 'var(--color-text-secondary)',
            margin: 0,
            lineHeight: 1.55,
          }}>
            {proposal.summary}
          </p>
        )}
      </div>

      {proposal.questions.map((q) => {
        const selected = selections[q.id] || []
        return (
          <div
            key={q.id}
            style={{
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: compact ? '0.625rem' : '0.875rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--color-accent)',
                background: 'color-mix(in srgb, var(--color-accent) 12%, transparent)',
                borderRadius: 'var(--radius-full, 999px)',
                padding: '0.125rem 0.5rem',
              }}>
                {q.header}
              </span>
              {q.multi && (
                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                  select all that apply
                </span>
              )}
            </div>
            <p style={{ fontSize: '1.0625rem', fontWeight: 500, margin: '0 0 0.5rem', lineHeight: 1.45 }}>
              {q.question}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {q.options.map((opt) => {
                const isOn = selected.includes(opt.label)
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => toggleOption(q.id, opt.label, q.multi)}
                    disabled={busy}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      textAlign: 'left',
                      padding: '0.5rem 0.625rem',
                      background: isOn
                        ? 'color-mix(in srgb, var(--color-accent) 8%, var(--color-bg))'
                        : 'var(--color-bg)',
                      border: `1px solid ${isOn ? 'var(--color-accent)' : 'var(--color-border)'}`,
                      borderRadius: 'var(--radius-md)',
                      cursor: busy ? 'default' : 'pointer',
                      color: 'var(--color-text)',
                      width: '100%',
                    }}
                  >
                    <span style={{
                      flexShrink: 0,
                      width: 16,
                      height: 16,
                      marginTop: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: q.multi ? 4 : '50%',
                      border: `1.5px solid ${isOn ? 'var(--color-accent)' : 'var(--color-border)'}`,
                      background: isOn ? 'var(--color-accent)' : 'transparent',
                      color: '#fff',
                    }}>
                      {isOn && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '1rem', fontWeight: 600, lineHeight: 1.35 }}>
                        {opt.label}
                      </span>
                      {opt.description && (
                        <span style={{
                          display: 'block',
                          fontSize: '0.9375rem',
                          color: 'var(--color-text-secondary)',
                          lineHeight: 1.4,
                          marginTop: 1,
                        }}>
                          {opt.description}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}

              {(customs[q.id] || []).map((c) => (
                <div
                  key={c}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.625rem',
                    background: 'color-mix(in srgb, var(--color-accent) 8%, var(--color-bg))',
                    border: '1px dashed var(--color-accent)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  <Check size={13} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: '1rem', fontWeight: 500 }}>{c}</span>
                  <button
                    type="button"
                    onClick={() => removeCustom(q.id, c)}
                    disabled={busy}
                    title="Remove"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                      color: 'var(--color-text-secondary)', display: 'inline-flex',
                    }}
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem' }}>
              <input
                type="text"
                value={customDrafts[q.id] || ''}
                onChange={(e) => setCustomDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addCustom(q.id)
                  }
                }}
                placeholder="Add a point the proposal missed…"
                disabled={busy}
                style={{
                  flex: 1,
                  padding: '0.375rem 0.5rem',
                  fontSize: '1rem',
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text)',
                }}
              />
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => addCustom(q.id)}
                disabled={busy || !(customDrafts[q.id] || '').trim()}
              >
                <Plus size={13} />
                Add
              </button>
            </div>
          </div>
        )
      })}

      <div>
        <label style={{
          display: 'block',
          fontSize: '1rem',
          fontWeight: 500,
          marginBottom: '0.25rem',
        }}>
          Anything else to include or clarify?
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={busy}
          placeholder="Points missed above, complexity notes, must-have rules… (optional)"
          style={{
            width: '100%',
            minHeight: compact ? 52 : 68,
            padding: '0.5rem 0.625rem',
            fontSize: '1rem',
            lineHeight: 1.5,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            resize: 'vertical',
            fontFamily: 'var(--font-primary)',
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={busy}
        >
          {busy ? (
            <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <ChevronRight size={15} />
          )}
          {confirmLabel}
        </button>
        {onSkip && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onSkip}
            disabled={busy}
            title="Skip this step and generate directly from the description"
          >
            Skip — generate as described
          </button>
        )}
      </div>
    </div>
  )
}
